// API service layer for SolvTax
// All API calls will be centralized here for easy backend integration

import { getSolvtaxApiOrigin } from "../config/apiBaseUrl";
import { collectClientContext } from "../lib/clientContext";

const getApiBaseUrl = () => `${getSolvtaxApiOrigin()}/api`;

const getTriageApiBaseUrl = () =>
  process.env.REACT_APP_TRIAGE_API_URL || `${getSolvtaxApiOrigin()}/api/v1/triage`;

const getUploadApiBaseUrl = () =>
  process.env.REACT_APP_UPLOAD_API_URL || `${getSolvtaxApiOrigin()}/api/v1/upload`;

const getLeadsApiBaseUrl = () =>
  process.env.REACT_APP_LEADS_API_URL || `${getSolvtaxApiOrigin()}/api/v1/leads`;

const getPayloadForLog = (options) => {
  if (!options.body) return undefined;

  if (typeof FormData !== "undefined" && options.body instanceof FormData) {
    const entries = {};
    for (const [key, value] of options.body.entries()) {
      if (typeof File !== "undefined" && value instanceof File) {
        entries[key] = { name: value.name, size: value.size, type: value.type };
      } else {
        entries[key] = value;
      }
    }
    return { formData: entries };
  }

  if (typeof options.body === "string") {
    try {
      return JSON.parse(options.body);
    } catch {
      return options.body;
    }
  }

  return options.body;
};

const fetchWithLogging = async (url, options = {}) => {
  const method = options.method || "GET";
  const payloadForLog = getPayloadForLog(options);

  console.log(`[API OUTGOING] ${method} ${url}`, payloadForLog ?? "(no body)");

  let response;
  try {
    response = await fetch(url, options);
  } catch (networkError) {
    console.error("[FETCH CRITICAL FAILURE]:", {
      url,
      method,
      requestPayload: payloadForLog,
      error: networkError,
    });
    throw networkError;
  }

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    console.error(`[API ERROR] ${response.status} on ${url}`, {
      method,
      requestPayload: payloadForLog,
      responseBody: body,
      details: body?.details,
      message: body?.message,
    });
    const error = new Error(
      body?.details || body?.message || body?.error || `Server returned ${response.status}`
    );
    error.status = response.status;
    error.body = body;
    throw error;
  }

  console.log(`[API SUCCESS] ${method} ${url}`, body);
  return body;
};

// Generic API request helper
const apiRequest = async (endpoint, options = {}) => {
  const url = `${getApiBaseUrl()}${endpoint}`;
  const config = {
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  };

  // Add auth token if available
  const token = localStorage.getItem("solvtax_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  try {
    return await fetchWithLogging(url, config);
  } catch (error) {
    console.error("[API REQUEST ERROR]:", { url, error });
    throw error;
  }
};

// Request helper scoped to the triage engine. Surfaces the parsed error body
// (e.g. zod validation details) instead of just the status code so the
// wizard can show a useful message.
const triageRequest = async (endpoint, options = {}) => {
  const url = `${getTriageApiBaseUrl()}${endpoint}`;
  const config = {
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  };

  try {
    return await fetchWithLogging(url, config);
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(
        `Could not reach the triage API at ${getTriageApiBaseUrl()}. Is solvtax-api running? (${
          error.message
        })`
      );
    }
    throw error;
  }
};

// Authentication API
export const authAPI = {
  login: async (email, password) => {
    // TODO: Implement login API call
    return apiRequest("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },

  register: async (userData) => {
    // TODO: Implement registration API call
    return apiRequest("/auth/register", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  },

  logout: async () => {
    // TODO: Implement logout API call
    return apiRequest("/auth/logout", {
      method: "POST",
    });
  },

  forgotPassword: async (email) => {
    // TODO: Implement forgot password API call
    return apiRequest("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },
};

// User Profile API
export const userAPI = {
  getProfile: async () => {
    // TODO: Implement get profile API call
    return apiRequest("/user/profile");
  },

  updateProfile: async (profileData) => {
    // TODO: Implement update profile API call
    return apiRequest("/user/profile", {
      method: "PUT",
      body: JSON.stringify(profileData),
    });
  },
};

// Filing API
export const filingAPI = {
  createFiling: async (filingData) => {
    // TODO: Implement create filing API call
    return apiRequest("/filings", {
      method: "POST",
      body: JSON.stringify(filingData),
    });
  },

  getFiling: async (filingId) => {
    // TODO: Implement get filing API call
    return apiRequest(`/filings/${filingId}`);
  },

  updateFiling: async (filingId, filingData) => {
    // TODO: Implement update filing API call
    return apiRequest(`/filings/${filingId}`, {
      method: "PUT",
      body: JSON.stringify(filingData),
    });
  },

  submitFiling: async (filingId) => {
    // TODO: Implement submit filing API call
    return apiRequest(`/filings/${filingId}/submit`, {
      method: "POST",
    });
  },

  getFilingHistory: async () => {
    // TODO: Implement get filing history API call
    return apiRequest("/filings/history");
  },
};

// Triage Engine API (solvtax-api, Modules A-D)
// Mirrors apps/solvtax-api/src/routes/triage.ts. The pipeline pauses at
// AWAITING_ACCOUNT after the audit; call authorize() once the user has a
// valid account to resume assembly and auto-filing. Poll getStatus() to
// follow the state machine (PENDING_INGESTION -> EXTRACTING -> AUDITING ->
// AWAITING_ACCOUNT -> READY_FOR_APPEAL -> FILED, or FAILED at any stage).
export const triageAPI = {
  // payload: { businessId, assessmentNoticeUrl, assetLedgerUrl, callbackUrl? }
  // Returns { jobId, state: "PENDING_INGESTION" } (HTTP 202).
  submit: async (payload) => {
    return triageRequest("/submit", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  // Returns the full JobStatus record (state, failure info, documents, etc.)
  // or throws a 404-flagged error if the job does not exist.
  getStatus: async (jobId) => {
    return triageRequest(`/${jobId}`);
  },

  // Resumes packet assembly + auto-filing after AWAITING_ACCOUNT (HTTP 202).
  authorize: async (jobId) => {
    return triageRequest(`/${jobId}/authorize`, {
      method: "POST",
    });
  },

  // Fire-and-forget hero calculator telemetry (Discord lead-activity hook).
  trackCalculator: async ({ vertical, assetValue, projectedSavings, context }) => {
    try {
      await triageRequest("/telemetry/calculator", {
        method: "POST",
        body: JSON.stringify({
          vertical,
          assetValue,
          projectedSavings,
          context: context ?? collectClientContext(),
        }),
      });
    } catch (err) {
      console.debug("Calculator telemetry skipped:", err.message);
    }
  },

  // Fire-and-forget high-signal site activity (Discord only; no DB write).
  trackActivity: async ({ event, path, label, meta, context }) => {
    try {
      await triageRequest("/telemetry/activity", {
        method: "POST",
        body: JSON.stringify({
          event,
          path,
          label,
          meta,
          context: context ?? collectClientContext(),
        }),
      });
    } catch (err) {
      console.debug("Site activity telemetry skipped:", err.message);
    }
  },
};

// Lead Capture API (corporate portal waitlist / lead funnel)
export const leadsAPI = {
  capture: async (payload) => {
    const url = `${getLeadsApiBaseUrl()}/capture`;

    try {
      return await fetchWithLogging(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      if (error instanceof TypeError) {
        throw new Error(
          `Could not reach the leads API at ${getLeadsApiBaseUrl()}. Is solvtax-api running? (${
            error.message
          })`
        );
      }
      throw error;
    }
  },

  waitlist: async (payload) => {
    const url = `${getLeadsApiBaseUrl()}/waitlist`;
    const formData = new FormData();
    formData.append("sessionId", payload.sessionId);
    formData.append("fullName", payload.fullName);
    formData.append("businessEmail", payload.businessEmail);
    formData.append("companyName", payload.companyName);
    if (payload.estimatedAssetValue) {
      formData.append("estimatedAssetValue", payload.estimatedAssetValue);
    }
    if (payload.website_url_hp !== undefined) {
      formData.append("website_url_hp", payload.website_url_hp ?? "");
    }
    if (payload["cf-turnstile-response"]) {
      formData.append("cf-turnstile-response", payload["cf-turnstile-response"]);
    }
    if (payload.noticeFile) {
      formData.append("notice", payload.noticeFile);
    }

    try {
      return await fetchWithLogging(url, {
        method: "POST",
        body: formData,
      });
    } catch (error) {
      if (error instanceof TypeError) {
        throw new Error(
          `Could not reach the leads API at ${getLeadsApiBaseUrl()}. Is solvtax-api running? (${
            error.message
          })`
        );
      }
      throw error;
    }
  },
};

// Document Upload API (solvtax-api multipart intake)
export const documentAPI = {
  uploadDocument: async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      return await fetchWithLogging(getUploadApiBaseUrl(), {
        method: "POST",
        body: formData,
      });
    } catch (error) {
      if (error instanceof TypeError) {
        throw new Error(
          `Could not reach the upload API at ${getUploadApiBaseUrl()}. Is solvtax-api running? (${
            error.message
          })`
        );
      }
      throw error;
    }
  },

  extractAssets: async (documentId) => {
    // TODO: Implement AI asset extraction API call
    return apiRequest(`/documents/${documentId}/extract`, {
      method: "POST",
    });
  },

  deleteDocument: async (documentId) => {
    // TODO: Implement delete document API call
    return apiRequest(`/documents/${documentId}`, {
      method: "DELETE",
    });
  },
};

// Jurisdiction API
export const jurisdictionAPI = {
  getJurisdictions: async () => {
    // TODO: Implement get jurisdictions API call
    return apiRequest("/jurisdictions");
  },

  detectJurisdictions: async (businessAddress) => {
    // TODO: Implement jurisdiction detection API call
    return apiRequest("/jurisdictions/detect", {
      method: "POST",
      body: JSON.stringify({ address: businessAddress }),
    });
  },

  getJurisdictionRequirements: async (jurisdictionId) => {
    // TODO: Implement get jurisdiction requirements API call
    return apiRequest(`/jurisdictions/${jurisdictionId}/requirements`);
  },
};

// Payment API
export const paymentAPI = {
  getPaymentMethods: async () => {
    // TODO: Implement get payment methods API call
    return apiRequest("/payment/methods");
  },

  addPaymentMethod: async (paymentData) => {
    // TODO: Implement add payment method API call
    return apiRequest("/payment/methods", {
      method: "POST",
      body: JSON.stringify(paymentData),
    });
  },

  deletePaymentMethod: async (paymentMethodId) => {
    // TODO: Implement delete payment method API call
    return apiRequest(`/payment/methods/${paymentMethodId}`, {
      method: "DELETE",
    });
  },

  processPayment: async (paymentData) => {
    // TODO: Implement process payment API call
    return apiRequest("/payment/process", {
      method: "POST",
      body: JSON.stringify(paymentData),
    });
  },

  getBillingHistory: async () => {
    // TODO: Implement get billing history API call
    return apiRequest("/payment/history");
  },
};

// Analytics/Dashboard API
export const analyticsAPI = {
  getDashboardData: async () => {
    // TODO: Implement get dashboard data API call
    return apiRequest("/analytics/dashboard");
  },

  getComplianceStatus: async () => {
    // TODO: Implement get compliance status API call
    return apiRequest("/analytics/compliance");
  },
};

// Support API
export const supportAPI = {
  submitSupportRequest: async (requestData) => {
    // TODO: Implement submit support request API call
    return apiRequest("/support/requests", {
      method: "POST",
      body: JSON.stringify(requestData),
    });
  },

  getChatHistory: async () => {
    // TODO: Implement get chat history API call
    return apiRequest("/support/chat/history");
  },

  sendChatMessage: async (message) => {
    // TODO: Implement send chat message API call
    return apiRequest("/support/chat/send", {
      method: "POST",
      body: JSON.stringify({ message }),
    });
  },
};

// Contact Form API (for marketing site)
export const contactAPI = {
  submitContactForm: async (contactData) => {
    // TODO: Implement submit contact form API call
    return apiRequest("/contact/submit", {
      method: "POST",
      body: JSON.stringify(contactData),
    });
  },

  subscribeToWaitlist: async (email) => {
    // TODO: Implement waitlist subscription API call
    return apiRequest("/contact/waitlist", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },
};

// Export default object with all APIs
export default {
  auth: authAPI,
  user: userAPI,
  filing: filingAPI,
  triage: triageAPI,
  leads: leadsAPI,
  document: documentAPI,
  jurisdiction: jurisdictionAPI,
  payment: paymentAPI,
  analytics: analyticsAPI,
  support: supportAPI,
  contact: contactAPI,
};
